import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { INLINE_BLOCK_EDITOR_NAME, useEditorsMap } from '@providers/editors-map';

import { DOCX_FILE_EXTENSION } from '../constants';
import { useClauseDocxImport } from '../hooks/useClauseDocxImport';
import type { ClauseDocxImportOutcome } from '../types';
import { DocxDropZone } from '../views/DocxDropZone';
import { ImportFileToolbarButton } from '../views/ImportFileToolbarButton';
import { showImportErrorToast } from '../views/ImportToasts';

interface ClauseDocxImportControlProps {
  outcome: ClauseDocxImportOutcome;
}

export const ClauseDocxImportControl = ({ outcome }: ClauseDocxImportControlProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isZoneOpen, setIsZoneOpen] = useState(false);
  const { importDocxFile, isConverting } = useClauseDocxImport(outcome);
  const { getEditor } = useEditorsMap();

  const editor = getEditor(INLINE_BLOCK_EDITOR_NAME);
  const blockElement = editor?.view.dom.closest<HTMLElement>('form') ?? null;

  const handleFiles = useCallback(
    async (files: FileList) => {
      if (isConverting) {
        return;
      }

      if (files.length > 1) {
        showImportErrorToast('multipleFiles');
        return;
      }

      const file = files[0];

      if (file && (await importDocxFile(file))) {
        setIsZoneOpen(false);
      }
    },
    [importDocxFile, isConverting],
  );

  useEffect(() => {
    if (!editor || !isConverting) {
      return;
    }

    editor.setEditable(false);
    return () => editor.setEditable(true);
  }, [editor, isConverting]);

  useEffect(() => {
    if (!isZoneOpen || !blockElement) {
      return;
    }

    blockElement.dataset.docxImport = 'open';
    return () => {
      delete blockElement.dataset.docxImport;
    };
  }, [isZoneOpen, blockElement]);

  useEffect(() => {
    if (!blockElement) {
      return;
    }

    const handleDragEnter = (event: DragEvent) => {
      if (Array.from(event.dataTransfer?.types ?? []).includes('Files')) {
        setIsZoneOpen(true);
      }
    };

    blockElement.addEventListener('dragenter', handleDragEnter);
    return () => blockElement.removeEventListener('dragenter', handleDragEnter);
  }, [blockElement]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleFiles(event.target.files);
    }

    event.target.value = '';
  };

  return (
    <>
      <input
        ref={inputRef}
        accept={DOCX_FILE_EXTENSION}
        className="hidden"
        data-testid="clause-docx-toolbar-import-input"
        onChange={handleInputChange}
        type="file"
      />
      <ImportFileToolbarButton
        isActive={isZoneOpen}
        isConverting={isConverting}
        onClick={() => (blockElement ? setIsZoneOpen((open) => !open) : inputRef.current?.click())}
      />
      {isZoneOpen &&
        blockElement &&
        createPortal(
          <DocxDropZone
            isConverting={isConverting}
            onClose={() => setIsZoneOpen(false)}
            onFilesDropped={handleFiles}
            onUploadClick={() => inputRef.current?.click()}
          />,
          blockElement,
        )}
    </>
  );
};
